import React from 'react';
import { getBadgeConfig, type GuideBadgeKey } from '@/app/config/badges';

interface GuideBadgeProps {
  /** Der eindeutige Key des Badges, z.B. 'tv_experience'. */
  badgeKey: GuideBadgeKey;
}

/**
 * Rendert ein einzelnes Guide-Badge (Chip/Pill) mit Icon, Label und Tooltip.
 * Holt die Konfiguration basierend auf dem badgeKey.
 */
export function GuideBadge({ badgeKey }: GuideBadgeProps) {
  // Konfiguration für diesen Badge-Key holen
  const config = getBadgeConfig(badgeKey);

  // Fallback, falls der Key ungültig ist oder die Config fehlt
  if (!config) {
    // Im Entwicklungsmodus eine Warnung ausgeben
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[GuideBadge] Konfiguration für Badge-Key "${badgeKey}" nicht gefunden.`);
    }
    return null; // Rendere nichts, wenn die Config fehlt
  }

  // Das Icon-Component aus der Konfiguration holen
  const Icon = config.iconComponent;

  // Tailwind-Klassen für das Badge zusammenstellen
  // Basis-Styling + dynamische Farbklassen aus der Config
  const badgeClasses = `
    inline-flex items-center align-middle  // Zentriert Icon und Text vertikal
    rounded-full                         // Runde Ecken für Chip-Look
    px-2.5 py-0.5                          // Padding innen
    text-xs font-medium                    // Textgröße und -gewicht
    mr-1 mb-1                              // Abstand zu anderen Badges/Elementen
    ${config.colorClasses}                 // Farbklassen aus der Config (bg, text, border)
  `;

  // Tailwind-Klassen für das Icon
  const iconClasses = "h-3.5 w-3.5 mr-1"; // Größe und Abstand zum Text

  return (
    // Native title-Attribut für einfachen Tooltip (wird durch config.tooltip befüllt)
    <span className={badgeClasses.trim()} title={config.tooltip}>
      {/* Das Icon rendern */}
      <Icon className={iconClasses} aria-hidden="true" />
      {/* Das Label rendern */}
      {config.label}
    </span>
  );
}